import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { getAdminUser, addAdminUserBonusJobSlots, isApiConfigured } from "../apiClient";

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

function signupTypeLabel(signupType) {
  return signupType === "hiring-manager" ? "Hiring manager" : "Applicant";
}

function billingTypeLabel(billingType) {
  return billingType === "subscription" ? "Subscription" : "Pay as you go";
}

function joinCodes(codes) {
  if (!Array.isArray(codes) || codes.length === 0) return "—";
  return codes.join(", ");
}

function DetailSection({ title, children }) {
  return (
    <section className="jw-admin-user-detail__section">
      <h2 className="jw-admin-user-detail__section-title">{title}</h2>
      <dl className="jw-admin-user-detail__grid">{children}</dl>
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value ?? "—"}</dd>
    </>
  );
}

function formatSocialProfiles(socialProfiles) {
  if (!socialProfiles || typeof socialProfiles !== "object") return "—";
  const parts = [];
  for (const key of ["linkedin", "instagram", "youtube"]) {
    const v = String(socialProfiles[key] || "").trim();
    if (v) parts.push(`${key}: ${v}`);
  }
  const customs = Array.isArray(socialProfiles.customs) ? socialProfiles.customs : [];
  for (const row of customs) {
    const name = String(row?.platformName || row?.platform || "").trim();
    const url = String(row?.url || "").trim();
    if (name || url) parts.push(`${name || "Custom"}: ${url || "—"}`);
  }
  return parts.length ? parts.join(" · ") : "—";
}

export default function AdminUserDetailPage({ currentUser, authToken, onSignOut }) {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bonusSlots, setBonusSlots] = useState("1");
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [bonusError, setBonusError] = useState("");

  const loadUser = useCallback(async () => {
    const t = String(authToken || "").trim();
    const id = String(userId || "").trim();
    if (!t || !isApiConfigured() || !id) {
      setError("Not signed in.");
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getAdminUser(t, id);
      setUser(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load this user.");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authToken, userId]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const openBonusModal = () => {
    setBonusSlots("1");
    setBonusError("");
    setBonusModalOpen(true);
  };

  const closeBonusModal = () => {
    if (bonusLoading) return;
    setBonusModalOpen(false);
    setBonusError("");
  };

  useEffect(() => {
    if (!bonusModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !bonusLoading) {
        setBonusModalOpen(false);
        setBonusError("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [bonusModalOpen, bonusLoading]);

  const handleAddBonusJobSlots = async (event) => {
    event.preventDefault();
    const t = String(authToken || "").trim();
    const id = String(userId || "").trim();
    const count = Math.floor(Number(bonusSlots));
    if (!t || !id || !Number.isFinite(count) || count <= 0) {
      setBonusError("Enter at least 1 job slot to add.");
      return;
    }

    setBonusLoading(true);
    setBonusError("");
    try {
      const data = await addAdminUserBonusJobSlots(t, id, count);
      setUser((prev) =>
        prev?.billing
          ? {
              ...prev,
              billing: {
                ...prev.billing,
                prepaidJobSlots: Math.max(0, Math.floor(Number(data?.prepaidJobSlots) || 0)),
              },
            }
          : prev
      );
      setBonusModalOpen(false);
    } catch (e) {
      setBonusError(e instanceof Error ? e.message : "Could not add job slots.");
    } finally {
      setBonusLoading(false);
    }
  };

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const rp = user?.recruitmentProfile;
  const isHiring = user?.role === "hiring-manager";

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin/users" className="jw-admin__back">
        ← Back to user search
      </Link>

      {loading ? <p className="jw-admin__muted">Loading user…</p> : null}

      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && user ? (
        <>
          <header className="jw-admin-user-detail__header">
            <h1 className="jw-admin__title">{user.fullName || user.email}</h1>
            <p className="jw-admin-user-detail__subtitle">
              <code>{user.email}</code>
              {" · "}
              {signupTypeLabel(user.signupType)}
            </p>
            <p className="jw-admin-user-detail__audit-link">
              <Link to={`/admin/audit?subjectUserId=${encodeURIComponent(String(user.id))}`}>
                Recent activity on this account
              </Link>
            </p>
          </header>

          <DetailSection title="Account">
            <DetailRow label="User ID" value={<code>{user.id}</code>} />
            <DetailRow label="Account type" value={signupTypeLabel(user.signupType)} />
            <DetailRow label="Joined" value={formatWhen(user.createdAt)} />
            <DetailRow label="Last updated" value={formatWhen(user.updatedAt)} />
          </DetailSection>

          <DetailSection title="Contact">
            <DetailRow label="Email" value={<code>{user.email}</code>} />
            <DetailRow
              label="Mobile"
              value={
                user.mobile
                  ? `${user.countryCode || ""} ${user.mobile}`.trim()
                  : "—"
              }
            />
            <DetailRow label="Email verified" value={yesNo(user.emailVerified)} />
            <DetailRow label="Email verified at" value={formatWhen(user.emailVerifiedAt)} />
            <DetailRow label="Phone verified" value={yesNo(user.phoneVerified)} />
            <DetailRow label="Phone verified at" value={formatWhen(user.phoneVerifiedAt)} />
          </DetailSection>

          {!isHiring ? (
            <DetailSection title="Applicant profile">
              <DetailRow label="Profile complete" value={yesNo(user.profileComplete)} />
              <DetailRow label="Profile active" value={yesNo(user.profileActive)} />
              <DetailRow label="Date of birth" value={formatDate(user.dateOfBirth)} />
              <DetailRow label="Country of residence" value={user.countryOfResidence || "—"} />
              <DetailRow label="Town / city" value={user.townCityOfResidence || "—"} />
              <DetailRow label="Work countries" value={joinCodes(user.workCountries)} />
              <DetailRow label="Profile photo uploaded" value={yesNo(user.hasProfilePhoto)} />
              <DetailRow label="CV uploaded" value={yesNo(user.hasCv)} />
              <DetailRow label="CV file name" value={user.cvFileName || "—"} />
            </DetailSection>
          ) : null}

          {isHiring ? (
            <>
              <DetailSection title="Organization">
                <DetailRow label="Company name" value={user.companyName || "—"} />
                <DetailRow label="Company website" value={user.companyWebsite || "—"} />
                <DetailRow label="Operating country" value={user.defaultOperatingCountry || "—"} />
                <DetailRow label="Company logo uploaded" value={yesNo(user.hasCompanyLogo)} />
                <DetailRow label="Profile photo uploaded" value={yesNo(user.hasProfilePhoto)} />
              </DetailSection>

              <DetailSection title="Recruitment defaults">
                <DetailRow
                  label="Who can apply by default"
                  value={
                    rp?.defaultApplyAudience === "countries"
                      ? "Specific countries"
                      : "Anyone on JOBUWIN"
                  }
                />
                <DetailRow
                  label="Default apply countries"
                  value={joinCodes(rp?.defaultApplyCountryCodes)}
                />
                <DetailRow label="Default job advertising currency" value={rp?.defaultAdvertisingCurrency || "—"} />
                <DetailRow label="Default job country" value={rp?.defaultJobCountry || "—"} />
                <DetailRow label="Default job town / city" value={rp?.defaultJobTownCity || "—"} />
              </DetailSection>

              {user.billing ? (
                <DetailSection title="Billing">
                  <DetailRow label="Billing type" value={billingTypeLabel(user.billing.billingType)} />
                  <DetailRow
                    label="Prepaid job slots"
                    value={
                      <div className="jw-admin-user-bonus-slots">
                        <span className="jw-admin-user-bonus-slots__count">
                          {String(user.billing.prepaidJobSlots ?? 0)}
                        </span>
                        <button type="button" className="jw-admin__refresh" onClick={openBonusModal}>
                          Add job slots
                        </button>
                      </div>
                    }
                  />
                  <DetailRow label="Subscription plan" value={user.billing.subscriptionPlanName || "—"} />
                  <DetailRow
                    label="Subscription period start"
                    value={formatDate(user.billing.subscriptionPeriodStart)}
                  />
                  <DetailRow
                    label="Jobs used this period"
                    value={String(user.billing.subscriptionJobsUsed ?? 0)}
                  />
                </DetailSection>
              ) : null}

              {user.jobPosts ? (
                <DetailSection title="Job posts">
                  <DetailRow label="Total" value={String(user.jobPosts.total ?? 0)} />
                  <DetailRow label="Active" value={String(user.jobPosts.active ?? 0)} />
                  <DetailRow label="Draft" value={String(user.jobPosts.draft ?? 0)} />
                  <DetailRow label="Hidden" value={String(user.jobPosts.hidden ?? 0)} />
                  <DetailRow label="Closed" value={String(user.jobPosts.closed ?? 0)} />
                </DetailSection>
              ) : null}
            </>
          ) : null}

          <DetailSection title="Social profiles">
            <DetailRow label="Links" value={formatSocialProfiles(user.socialProfiles)} />
          </DetailSection>
        </>
      ) : null}

      {bonusModalOpen && user?.billing ? (
        <div className="jw-admin-modal-overlay" role="presentation" onClick={closeBonusModal}>
          <div
            className="jw-admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-bonus-job-slots-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="jw-admin-modal__close"
              onClick={closeBonusModal}
              aria-label="Close"
              disabled={bonusLoading}
            >
              ×
            </button>
            <h2 id="admin-bonus-job-slots-title" className="jw-admin-modal__title">
              Add job slots
            </h2>
            <p className="jw-admin-modal__lede">
              They currently have{" "}
              <strong>
                {user.billing.prepaidJobSlots ?? 0} job slot
                {Number(user.billing.prepaidJobSlots) === 1 ? "" : "s"}
              </strong>
              . Enter how many to add — it&apos;ll show up in their billing history as bonus jobs from the JOBUWIN
              team.
            </p>
            <form className="jw-admin-modal__form" onSubmit={handleAddBonusJobSlots}>
              <label className="jw-admin-modal__field" htmlFor="admin-bonus-job-slots">
                <span className="jw-admin-modal__label">Job slots to add</span>
                <input
                  id="admin-bonus-job-slots"
                  type="number"
                  className="jw-admin-rates__input jw-admin-modal__input"
                  min={1}
                  max={100}
                  step={1}
                  value={bonusSlots}
                  onChange={(event) => setBonusSlots(event.target.value)}
                  disabled={bonusLoading}
                  autoFocus
                />
              </label>
              {bonusError ? (
                <p className="jw-admin__error" role="alert">
                  {bonusError}
                </p>
              ) : null}
              <div className="jw-admin-modal__actions">
                <button type="button" className="jw-admin-modal__cancel" onClick={closeBonusModal} disabled={bonusLoading}>
                  Cancel
                </button>
                <button type="submit" className="jw-admin__refresh" disabled={bonusLoading}>
                  {bonusLoading ? "Adding…" : "Add job slots"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
